import React, { useState, useMemo } from 'react';
import { Mail, Loader2, Eye, Send, X, ArrowLeft, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ConferenceNotifier = ({ registrations = [], getPassword, onSent }) => {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const allSelected = registrations.length > 0 && selectedIds.length === registrations.length;

  const filteredRegs = useMemo(() => {
    if (!recipientSearch.trim()) return registrations;
    const q = recipientSearch.toLowerCase();
    return registrations.filter((r) =>
      (r.first_name || '').toLowerCase().includes(q) ||
      (r.last_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  }, [registrations, recipientSearch]);

  const toggleId = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : registrations.map((r) => r.id));
  };

  const reset = () => {
    setOpen(false);
    setPreviewHtml(null);
    setSubject('');
    setMessage('');
    setSelectedIds([]);
    setRecipientSearch('');
  };

  const handlePreview = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/conference-notifications/preview?password=${encodeURIComponent(getPassword())}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, message, registration_ids: selectedIds }),
        }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Preview failed');
      setPreviewHtml(d.html);
    } catch (e) {
      toast.error(e.message || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one attendee');
      return;
    }
    const scope = allSelected ? `ALL ${registrations.length} registered attendees` : `${selectedIds.length} selected attendee(s)`;
    if (!window.confirm(`Send this email to ${scope}?`)) return;
    setSending(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/conference-notifications/send?password=${encodeURIComponent(getPassword())}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, message, registration_ids: selectedIds, send_all: allSelected }),
        }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Email sent to ${d.sent} of ${d.total} attendee(s)${d.failed ? ` · ${d.failed} failed` : ''}`);
      if (d.failed && d.error) toast.error(`Reason for failures: ${d.error}`);
      reset();
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disabled={registrations.length === 0}
        className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold"
        data-testid="notify-attendees-btn"
      >
        <Mail className="w-4 h-4 mr-2" /> Email Attendees
      </Button>

      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="notify-modal">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="bg-brand-navy text-white p-5 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">{previewHtml ? 'Email Preview' : 'Email Conference Attendees'}</h2>
                <p className="text-white/70 text-sm">
                  {previewHtml
                    ? 'This is exactly how the email will look in the recipient\'s inbox.'
                    : 'Compose a branded notification. Select recipients, then preview before sending.'}
                </p>
              </div>
              <button onClick={reset} className="text-white/80 hover:text-white" data-testid="notify-close">
                <X className="w-6 h-6" />
              </button>
            </div>

            {previewHtml ? (
              <>
                <div className="flex-1 overflow-auto bg-slate-100 p-4">
                  <iframe
                    title="Email preview"
                    srcDoc={previewHtml}
                    className="w-full bg-white rounded-xl border border-navy-100"
                    style={{ minHeight: '60vh' }}
                    data-testid="notify-preview-frame"
                  />
                </div>
                <div className="border-t border-navy-100 p-4 flex justify-between items-center gap-3 flex-wrap">
                  <Button variant="outline" onClick={() => setPreviewHtml(null)}
                    className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                    data-testid="notify-back-to-edit">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Edit
                  </Button>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-navy-500">
                      Sending to <strong className="text-navy-900">{selectedIds.length}</strong> attendee(s)
                    </span>
                    <Button onClick={handleSend} disabled={sending || selectedIds.length === 0}
                      className="bg-brand-green hover:bg-green-700 text-white font-semibold"
                      data-testid="notify-send-btn">
                      {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-auto grid md:grid-cols-5 gap-0">
                  {/* Recipients */}
                  <div className="md:col-span-2 border-r border-navy-100 flex flex-col max-h-[65vh]">
                    <div className="p-4 border-b border-navy-100 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm font-semibold text-navy-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="w-4 h-4 accent-[#0A2540]"
                            data-testid="notify-select-all"
                          />
                          Select All ({registrations.length})
                        </label>
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-navy-50 text-navy-700" data-testid="notify-selected-count">
                          {selectedIds.length} selected
                        </span>
                      </div>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                        <Input
                          placeholder="Search attendees…"
                          value={recipientSearch}
                          onChange={(e) => setRecipientSearch(e.target.value)}
                          className="pl-9 h-9"
                          data-testid="notify-recipient-search"
                        />
                      </div>
                    </div>
                    <div className="flex-1 overflow-auto p-2" data-testid="notify-recipient-list">
                      {filteredRegs.length === 0 ? (
                        <p className="p-4 text-sm text-navy-400">No attendees match your search.</p>
                      ) : (
                        filteredRegs.map((r) => (
                          <label
                            key={r.id}
                            className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedIds.includes(r.id) ? 'bg-green-50' : 'hover:bg-navy-50'}`}
                            data-testid={`notify-recipient-${r.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(r.id)}
                              onChange={() => toggleId(r.id)}
                              className="w-4 h-4 mt-0.5 accent-[#0A2540]"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-navy-900 truncate">
                                {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                              </span>
                              <span className="block text-xs text-navy-500 truncate">{r.email}</span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Compose */}
                  <div className="md:col-span-3 p-5 space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-navy-800 mb-1 block">Subject</label>
                      <Input
                        placeholder="e.g., Agenda Update — 2026 Citywide Conference"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        data-testid="notify-subject"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-navy-800 mb-1 block">Message</label>
                      <Textarea
                        placeholder={"Write your update here…\n\nSeparate paragraphs with a blank line. Start with your own greeting (e.g., Dear Attendees,)."}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={12}
                        data-testid="notify-message"
                      />
                      <p className="text-xs text-navy-400 mt-1.5">
                        The email is automatically wrapped in the DC Men in ECE branded template with a conference details button and contact info.
                        Links are clickable automatically — paste any URL, or use <code className="text-navy-600 bg-navy-50 px-1 rounded">[Link Text](https://example.com)</code> for custom link text.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-navy-100 p-4 flex justify-end items-center gap-3 flex-wrap">
                  <Button variant="outline" onClick={handlePreview}
                    disabled={previewLoading || !subject.trim() || !message.trim()}
                    className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                    data-testid="notify-preview-btn">
                    {previewLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</> : <><Eye className="w-4 h-4 mr-2" /> Preview Email</>}
                  </Button>
                  <Button onClick={handleSend}
                    disabled={sending || selectedIds.length === 0 || !subject.trim() || !message.trim()}
                    className="bg-brand-green hover:bg-green-700 text-white font-semibold"
                    data-testid="notify-send-direct-btn">
                    {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to {selectedIds.length || 0}</>}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ConferenceNotifier;
